import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { ColumnDef } from "@tanstack/react-table";
import dayjs from "dayjs";
import { InspectionListItem } from "@/types";
import { useGetInspections } from "@/services/Inspections";
import { useInspectionListFilters, useInspectionsStore } from "@/stores/inspectionsStore";
import { useOrdersDrawerStore } from "@/stores/ordersDrawerStore";
import DataTable from "@/components/molecules/DataTable";
import SortableHeader from "@/components/molecules/SortableHeader";
import InspectOrderCTA from "@/components/molecules/InspectOrderCTA";
import NumberFormatter from "@/components/atoms/NumberFormatter";
import TradeIdentifier from "@/components/atoms/TradeIdentifier";
import Text from "@/components/atoms/Text";
import ContractTradeTypeEnum from "@/constants/enums/ContractTradeTypeEnum";
import styles from "./styles.module.css";

/** Orders waiting for the company to inspect them, oldest planned date first, filtered by the toolbar. */
export default function InspectionsTable() {
  const { t } = useTranslation();
  const filters = useInspectionListFilters();
  const { data: inspections = [], isLoading, isError } = useGetInspections(filters);
  const openOrdersDrawer = useOrdersDrawerStore((s) => s.open);
  const currentPage = useInspectionsStore((s) => s.currentPage);
  const setPage = useInspectionsStore((s) => s.setPage);
  const rowsPerPage = useInspectionsStore((s) => s.rowsPerPage);
  const setRowsPerPage = useInspectionsStore((s) => s.setRowsPerPage);
  // Rows also go away without a filter change (an inspection submitted), so never show a page past the last one.
  const activePage = Math.min(currentPage, Math.max(1, Math.ceil(inspections.length / rowsPerPage)));

  const columns = useMemo<ColumnDef<InspectionListItem>[]>(
    () => [
      {
        header: ({ column }) => <SortableHeader column={column} label={t("common:id")} />,
        id: "orderId",
        accessorKey: "orderId",
        size: 120,
        // The company inspects what it receives, so every row is a buy.
        cell: ({ row }) => <TradeIdentifier tradeType={ContractTradeTypeEnum.BUY} id={row.original.orderId} />,
      },
      {
        header: ({ column }) => <SortableHeader column={column} label={t("inspections:list.plannedDate")} />,
        id: "plannedDate",
        accessorKey: "plannedDate",
        // Same date style as the contract list, with the time added.
        cell: ({ row }) => (
          <Text size="md" color="tertiary">
            {dayjs(row.original.plannedDate).format("DD.MM.YYYY - HH:mm")}
          </Text>
        ),
      },
      {
        header: ({ column }) => <SortableHeader column={column} label={t("common:contract")} />,
        id: "contractName",
        accessorFn: (row) => row.contractName ?? "",
        cell: ({ row }) => row.original.contractName ?? "—",
      },
      {
        header: ({ column }) => <SortableHeader column={column} label={t("common:type")} />,
        id: "salesItemName",
        accessorFn: (row) => row.salesItemName ?? "",
        cell: ({ row }) => row.original.salesItemName ?? "—",
      },
      {
        header: ({ column }) => <SortableHeader column={column} label={t("inspections:list.supplier")} />,
        id: "supplierCompanyName",
        accessorKey: "supplierCompanyName",
      },
      {
        header: ({ column }) => <SortableHeader ta="right" column={column} label={t("common:amount")} />,
        id: "amount",
        accessorKey: "amount",
        cell: ({ row }) => (
          <Text ta="right">
            <NumberFormatter value={row.original.amount} suffix={` ${row.original.measureUnit}`} />
          </Text>
        ),
      },
      {
        header: ({ column }) => <SortableHeader column={column} label={t("dashboard:status")} />,
        id: "status",
        enableSorting: false,
        cell: ({ row }) => <InspectOrderCTA orderId={row.original.orderId} />,
      },
    ],
    [t]
  );

  const isEmpty = !isLoading && !isError && inspections.length === 0;
  const isFiltered = Object.keys(filters).length > 0;

  return (
    <div data-testid="inspections-table">
      <DataTable
        data={inspections}
        columns={columns}
        isLoading={isLoading}
        mobileHeaderCell={[0, 1, 4]}
        // The empty state takes the pagination's place, right under the header.
        showPagination={!isEmpty && !isError}
        activePage={activePage}
        onPageChange={setPage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={setRowsPerPage}
        onRowClick={(row) => openOrdersDrawer({ orderId: row.original.orderId })}
      />
      {isError && (
        <div className={styles.emptyState} data-testid="inspections-error">
          <Text size="sm" color="error">
            {t("inspections:list.loadError")}
          </Text>
        </div>
      )}
      {isEmpty && (
        <div className={styles.emptyState} data-testid="inspections-empty">
          <Text size="md" strong>
            {isFiltered ? t("inspections:empty.filtered") : t("inspections:empty.all")}
          </Text>
          <Text size="sm" color="tertiary">
            {isFiltered ? t("inspections:empty.filteredBody") : t("inspections:empty.body")}
          </Text>
        </div>
      )}
    </div>
  );
}
